import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Plus,
  Users,
  MapPin,
  Phone,
  Mail,
  Edit3,
  PanelRight,
  AlertTriangle,
  FileText,
  CheckCircle
} from "lucide-react";
import { format, isSameDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { CreateAppointmentModal } from "@/components/appointments/CreateAppointmentModal";
import { CreateTaskModal } from "@/components/tasks/CreateTaskModal";
import { AppointmentCalendar } from '@/components/appointments/AppointmentCalendar';
import { GoogleCalendarConnect } from '@/components/appointments/GoogleCalendarConnect';
import { requestGoogleCalendarSync, requestPortalSync } from '@/services/googleCalendarSync';

interface Appointment {
  id: string;
  title: string;
  appointment_date: string;
  duration: number;
  status: string;
  priority: string;
  appointment_type: string;
  color_tag: string;
  location?: string;
  description?: string;
  prospect_name?: string;
  clients?: { name: string };
  cases?: { title: string };
  video_conference_link?: string;
  case_id?: string;
}

interface AppointmentCompletion {
  id: string;
  appointment_id: string;
  completed_at: string;
  completion_report: string;
  completed_by: string;
}

interface Task {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  due_date?: string;
  assigned_to?: string;
  case_id?: string;
  created_at: string;
  cases?: { title: string };
}

const priorityColors: Record<string, string> = {
  "critica": "border-red-500 bg-red-50",
  "urgente": "border-orange-500 bg-orange-50", 
  "alta": "border-yellow-500 bg-yellow-50",
  "normal": "border-blue-500 bg-blue-50",
  "baixa": "border-green-500 bg-green-50"
};

const statusColors: Record<string, string> = {
  "Agendado": "bg-blue-500",
  "Confirmado": "bg-green-500", 
  "Em andamento": "bg-yellow-500",
  "Concluído": "bg-emerald-500",
  "Cancelado": "bg-red-500",
  "Reagendado": "bg-purple-500"
};

const Agenda = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [criticalDeadlines, setCriticalDeadlines] = useState<Task[]>([]); // Novo estado para prazos críticos
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [clickedDate, setClickedDate] = useState<Date | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionReport, setCompletionReport] = useState("");
  const [isCompleting, setIsCompleting] = useState(false);
  const [appointmentCompletion, setAppointmentCompletion] = useState<AppointmentCompletion | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchAppointments = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          *,
          clients:client_id (name),
          cases:case_id (title)
        `)
        .order('appointment_date', { ascending: true });

      if (error) throw error;
      setAppointments(data || []);
    } catch (error) {
      console.error('Erro ao buscar compromissos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os compromissos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchTasks = useCallback(async () => {
    // Tarefas concluídas marcadas para o cliente vão para o Portal do Cliente
    requestPortalSync();
    // Tarefas com prazo também vão para o Google Agenda (celular e tablet)
    requestGoogleCalendarSync();
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          cases:case_id (title)
        `)
        .eq('user_id', user.id)
        .order('due_date', { ascending: true });

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error('Erro ao buscar tarefas:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as tarefas",
        variant: "destructive"
      });
    }
  }, [toast]);

  const fetchCriticalDeadlines = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const today = new Date();
      const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      
      // Buscar prazos críticos (próximos 7 dias)
      const sevenDaysFromNow = new Date(today);
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
      const sevenDaysLocal = new Date(sevenDaysFromNow.getFullYear(), sevenDaysFromNow.getMonth(), sevenDaysFromNow.getDate());

      const { data: criticalTasks, error } = await supabase
        .from('tasks')
        .select(`
          *,
          cases:case_id (title)
        `)
        .eq('user_id', user.id)
        .neq('status', 'Concluída')
        .order('due_date', { ascending: true });

      if (error) throw error;

      const criticalDeadlinesData = criticalTasks?.filter(task => {
        if (!task.due_date) return false;
        
        const taskDate = new Date(task.due_date);
        const taskDateLocal = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
        
        return taskDateLocal.getTime() >= todayLocal.getTime() && taskDateLocal.getTime() <= sevenDaysLocal.getTime();
      }).slice(0, 5) || [];

      console.log('Prazos críticos encontrados:', criticalDeadlinesData);
      setCriticalDeadlines(criticalDeadlinesData);
    } catch (error) {
      console.error('Erro ao buscar prazos críticos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os prazos críticos",
        variant: "destructive"
      });
    }
  }, [toast]);

  useEffect(() => {
    const loadData = async () => {
      await fetchAppointments();
      await fetchTasks();
      await fetchCriticalDeadlines();
    };
    loadData();
  }, [fetchAppointments, fetchTasks, fetchCriticalDeadlines]);

  const getAppointmentsForDate = (date: Date) => {
    return appointments.filter(apt => {
      const aptDate = new Date(apt.appointment_date);
      const aptDateLocal = new Date(aptDate.getFullYear(), aptDate.getMonth(), aptDate.getDate());
      const targetDateLocal = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      return aptDateLocal.getTime() === targetDateLocal.getTime();
    }).sort((a, b) => new Date(a.appointment_date).getTime() - new Date(b.appointment_date).getTime());
  };

  const getTasksForDate = (date: Date) => {
    return tasks.filter(task => {
      if (!task.due_date) return false;
      
      const taskDate = new Date(task.due_date);
      const taskDateLocal = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
      const targetDateLocal = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      return taskDateLocal.getTime() === targetDateLocal.getTime();
    }).sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());
  };

  const getOverdueTasks = () => {
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    return tasks.filter(task => {
      if (!task.due_date || task.status === 'Concluída') return false;
      
      const taskDate = new Date(task.due_date);
      const taskDateLocal = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
      
      return taskDateLocal.getTime() < todayLocal.getTime();
    }).sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());
  };

  const handleAppointmentClick = async (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    
    // Se o compromisso está concluído, buscar detalhes da finalização
    if (appointment.status === 'Concluído') {
      await fetchAppointmentCompletion(appointment.id);
    }
    
    setShowAppointmentModal(true);
  };

  const fetchAppointmentCompletion = async (appointmentId: string) => {
    try {
      const { data, error } = await supabase
        .from('appointment_completions')
        .select('*')
        .eq('appointment_id', appointmentId)
        .single();

      if (error) throw error;
      setAppointmentCompletion(data);
    } catch (error) {
      console.error('Erro ao buscar detalhes da finalização:', error);
      setAppointmentCompletion(null);
    }
  };

  const handleDateClick = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      if (!isSidebarOpen) {
        setIsSidebarOpen(true);
      }
    }
  };
  
  const handleCreateAppointmentForDate = (date: Date) => {
    setClickedDate(date);
    setShowCreateModal(true);
  }

  const handleCreateTaskForDate = (date: Date) => {
    setClickedDate(date);
    setShowCreateTaskModal(true);
  }

  const handleAppointmentCreated = () => {
    fetchAppointments(); // Recarregar compromissos
    fetchTasks(); // Recarregar tarefas
    toast({
      title: "Sucesso!",
      description: "Compromisso criado e salvo na agenda"
    });
  };

  const handleTaskCreated = () => {
    fetchTasks(); // Recarregar tarefas
    toast({
      title: "Sucesso!",
      description: "Tarefa criada com sucesso"
    });
  };

  const handleEditAppointment = (appointment: Appointment) => {
    setEditingAppointment(appointment);
    setShowEditModal(true);
    setShowAppointmentModal(false); // Fechar modal de detalhes
  };

  const handleAppointmentUpdated = () => {
    fetchAppointments();
    setShowEditModal(false);
    setEditingAppointment(null);
    toast({
      title: "Sucesso!",
      description: "Compromisso atualizado com sucesso."
    });
  };

  const handleAppointmentDrop = async (appointmentId: string, newDate: Date) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ appointment_date: newDate.toISOString() })
        .eq('id', appointmentId);

      if (error) throw error;

      toast({
        title: "Sucesso!",
        description: "Compromisso reagendado com sucesso."
      });
      fetchAppointments(); // Re-fetch to update UI
      requestGoogleCalendarSync();
    } catch (error) {
      console.error("Erro ao reagendar compromisso:", error);
      toast({
        title: "Erro",
        description: "Não foi possível reagendar o compromisso.",
        variant: "destructive"
      });
    }
  };

  const handleCompleteAppointment = async () => {
    if (!selectedAppointment || !completionReport.trim()) return;

    setIsCompleting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Salvar na tabela appointment_completions
      const { error: completionError } = await supabase
        .from('appointment_completions')
        .insert({
          appointment_id: selectedAppointment.id,
          completion_report: completionReport.trim(),
          completed_by: user.id,
          completed_at: new Date().toISOString()
        });

      if (completionError) throw completionError;

      // Atualizar status do compromisso para "Concluído"
      const { error: updateError } = await supabase
        .from('appointments')
        .update({ status: 'Concluído' })
        .eq('id', selectedAppointment.id);

      if (updateError) throw updateError;

      toast({
        title: "Sucesso!",
        description: "Compromisso finalizado com sucesso."
      });

      // Fechar modais e limpar estados
      setShowCompletionModal(false);
      setShowAppointmentModal(false);
      setCompletionReport("");
      setSelectedAppointment(null);
      setAppointmentCompletion(null);
      
      // Recarregar dados
      fetchAppointments();
    } catch (error) {
      console.error("Erro ao finalizar compromisso:", error);
      toast({
        title: "Erro",
        description: "Não foi possível finalizar o compromisso.",
        variant: "destructive"
      });
    } finally {
      setIsCompleting(false);
    }
  };

  const formatTime = (dateString: string) => {
    return format(new Date(dateString), 'HH:mm');
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins > 0 ? `${mins}m` : ''}`;
    }
    return `${mins}m`;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'critica':
      case 'urgente':
        return 'border-red-500 bg-red-50';
      case 'alta':
        return 'border-orange-500 bg-orange-50';
      case 'média':
      case 'media':
        return 'border-yellow-500 bg-yellow-50';
      case 'baixa':
        return 'border-green-500 bg-green-50';
      default:
        return 'border-blue-500 bg-blue-50';
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'critica':
      case 'urgente':
        return { variant: 'destructive' as const, text: 'Urgente' };
      case 'alta':
        return { variant: 'secondary' as const, text: 'Alto' };
      case 'média':
      case 'media':
        return { variant: 'outline' as const, text: 'Médio' };
      case 'baixa':
        return { variant: 'outline' as const, text: 'Baixo' };
      default:
        return { variant: 'outline' as const, text: 'Normal' };
    }
  };

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const taskDate = new Date(dueDate);
    const taskDateLocal = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
    
    const diffTime = taskDateLocal.getTime() - todayLocal.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Vence hoje';
    if (diffDays === 1) return 'Vence amanhã';
    if (diffDays < 0) return `Vencido há ${Math.abs(diffDays)} dias`;
    return `Vence em ${diffDays} dias`;
  };

  const selectedDateAppointments = getAppointmentsForDate(selectedDate);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full gap-6 p-6">
      <div className="flex-1 flex flex-col space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Agenda Inteligente</h1>
            <p className="text-muted-foreground">
              Visualize, arraste e gerencie seus compromissos de forma integrada aos processos.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <GoogleCalendarConnect onSynced={fetchAppointments} />
            <Button
              onClick={() => handleCreateTaskForDate(new Date())}
              className="h-10 rounded-xl border border-slate-200 bg-white px-4 text-[13px] text-slate-800 shadow-none hover:border-[#c4ab83] hover:bg-[#faf7f2]"
            >
              <Plus className="mr-1.5 h-4 w-4 text-[#9a7f52]" />
              Nova tarefa
            </Button>
            <Button
              onClick={() => handleCreateAppointmentForDate(new Date())}
              className="h-10 rounded-xl bg-slate-900 px-4 text-[13px] text-white hover:bg-slate-800"
            >
              <Plus className="mr-1.5 h-4 w-4 text-[#e8d5a8]" />
              Novo compromisso
            </Button>
            <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden">
                  <PanelRight className="w-4 h-4" />
                </Button>
              </SheetTrigger>
            </Sheet>
          </div>
        </div>

        <div className="flex-1 min-h-0">
          <Card className="h-full bg-card/60 backdrop-blur-md border border-border/50 shadow-lg transition-all">
            <CardContent className="p-0 h-full">
              <DndProvider backend={HTML5Backend}>
                <AppointmentCalendar
                  appointments={appointments}
                  tasks={tasks}
                  selectedDate={selectedDate}
                  onDateSelect={handleDateClick}
                  onAppointmentSelect={handleAppointmentClick}
                  onAppointmentDrop={handleAppointmentDrop}
                  onCreateAppointment={handleCreateAppointmentForDate}
                  onCreateTask={handleCreateTaskForDate}
                />
              </DndProvider>
            </CardContent>
          </Card>
        </div>
      </div>

      <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <SheetContent className="w-[400px] sm:w-[540px] bg-card/80 backdrop-blur-lg p-0 border-l border-border/50 overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <SheetHeader className="p-6">
            <SheetTitle>Informações do Dia</SheetTitle>
          </SheetHeader>
          <div className="space-y-6 p-6 pt-0">
            <Card className="bg-transparent border-0 shadow-none">
              <CardHeader className="p-0 mb-4">
                <h3 className="flex items-center gap-2 text-lg font-semibold">
                  <Clock className="w-5 h-5" />
                  {format(selectedDate, "dd 'de' MMMM", { locale: ptBR })}
                  <Badge variant="outline" className="ml-auto">
                    {selectedDateAppointments.length} compromisso{selectedDateAppointments.length !== 1 ? 's' : ''}
                  </Badge>
                </h3>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-64">
                  {selectedDateAppointments.length > 0 ? (
                    <div className="space-y-3">
                      {selectedDateAppointments.map((appointment) => (
                        <div
                          key={appointment.id}
                          className={cn(
                            "p-3 rounded-lg border-l-4 hover:bg-accent cursor-pointer transition-colors relative",
                            priorityColors[appointment.priority] || "border-l-gray-300",
                            appointment.status === "Concluído" && "opacity-60"
                          )}
                          onClick={() => handleAppointmentClick(appointment)}
                        >
                          {appointment.status === "Concluído" && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="w-full h-0.5 bg-gray-400 opacity-70"></div>
                            </div>
                          )}
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm truncate">{appointment.title}</h4>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                <span>{formatTime(appointment.appointment_date)}</span>
                                <span>•</span>
                                <span>{formatDuration(appointment.duration)}</span>
                              </div>
                              {appointment.location && (
                                <div className="flex items-center gap-1 mt-1">
                                  <MapPin className="w-3 h-3" />
                                  <span className="text-xs text-muted-foreground truncate">
                                    {appointment.location}
                                  </span>
                                </div>
                              )}
                            </div>
                            <Badge className={`text-xs ${statusColors[appointment.status]} text-white ml-2`}>
                              {appointment.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Nenhum compromisso neste dia</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            <Separator />

            <Card className="bg-transparent border-0 shadow-none">
              <CardHeader className="p-0 mb-4">
                <h3 className="flex items-center gap-2 text-lg font-semibold">
                  <AlertTriangle className="w-5 h-5" />
                  Prazos Críticos
                </h3>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-64">
                  {criticalDeadlines.length > 0 ? (
                    <div className="space-y-3">
                      {criticalDeadlines.map((task) => (
                        <div
                          key={task.id}
                          className={cn(
                            "p-3 rounded-lg border-l-4 hover:bg-accent cursor-pointer transition-colors",
                            getPriorityColor(task.priority)
                          )}
                          onClick={() => window.location.href = '/tasks'}
                        >
                          <div className="space-y-2">
                            <div className="flex items-start justify-between">
                              <h4 className="font-medium text-sm">{task.title}</h4>
                              <Badge variant={getPriorityBadge(task.priority).variant} className="text-xs">
                                {getPriorityBadge(task.priority).text}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              <span>{getDaysUntilDue(task.due_date || '')}</span>
                            </div>
                            {task.description && (
                              <div className="text-xs text-muted-foreground truncate">
                                {task.description}
                              </div>
                            )}
                            {task.cases && (
                              <div className="text-xs text-muted-foreground">
                                Processo: {task.cases.title}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Nenhum prazo crítico encontrado</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            <Separator />

            <Card className="bg-transparent border-0 shadow-none">
              <CardHeader className="p-0 mb-4">
                <h3 className="flex items-center gap-2 text-lg font-semibold">
                  <FileText className="w-5 h-5" />
                  Tarefas Pendentes
                </h3>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-64">
                  {tasks.length > 0 ? (
                    <div className="space-y-3">
                      {tasks.filter(task => task.status !== 'Concluída').map((task) => {
                        const dueDate = task.due_date ? new Date(task.due_date) : null;
                        const today = new Date();
                        const isOverdue = dueDate && dueDate < today;
                        const daysUntilDue = dueDate ? Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) : null;
                        
                        return (
                          <div
                            key={task.id}
                            className={cn(
                              "p-3 rounded-lg border-l-4 hover:bg-accent cursor-pointer transition-colors",
                              isOverdue ? "border-l-red-500 bg-red-50" :
                              task.priority === 'Alta' ? "border-l-orange-500 bg-orange-50" :
                              task.priority === 'Média' ? "border-l-yellow-500 bg-yellow-50" :
                              "border-l-green-500 bg-green-50"
                            )}
                            onClick={() => window.location.href = '/tasks'}
                          >
                            <div className="space-y-2">
                              <div className="flex items-start justify-between">
                                <h4 className="font-medium text-sm">{task.title}</h4>
                                <Badge 
                                  variant={isOverdue ? "destructive" : "outline"} 
                                  className="text-xs"
                                >
                                  {task.priority}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                <span>
                                  {isOverdue 
                                    ? `Atrasada há ${Math.abs(daysUntilDue)} dia${Math.abs(daysUntilDue) !== 1 ? 's' : ''}`
                                    : dueDate 
                                      ? `Vence em ${daysUntilDue} dia${daysUntilDue !== 1 ? 's' : ''}`
                                      : 'Sem prazo'
                                  }
                                </span>
                              </div>
                              {task.description && (
                                <div className="text-xs text-muted-foreground truncate">
                                  {task.description}
                                </div>
                              )}
                              {task.cases && (
                                <div className="text-xs text-muted-foreground">
                                  Processo: {task.cases.title}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Nenhuma tarefa pendente</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal de Detalhes do Compromisso */}
      <Dialog open={showAppointmentModal} onOpenChange={setShowAppointmentModal}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden border border-[#c4ab83]/30 bg-white shadow-2xl">
          {selectedAppointment && (() => {
            const appointmentDate = new Date(selectedAppointment.appointment_date);
            const typeLabel = ({
              audiencia: 'Audiência', consulta: 'Consulta', reuniao: 'Reunião', atendimento: 'Atendimento', prazo: 'Prazo',
              depoimento: 'Depoimento', pericia: 'Perícia', mediacao: 'Mediação', conciliacao: 'Conciliação', diligencia: 'Diligência', outro: 'Compromisso',
            } as Record<string, string>)[String(selectedAppointment.appointment_type || '')] || 'Compromisso';
            const statusKey = String(selectedAppointment.status || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
            const statusTone = ({
              agendado: 'bg-blue-50 text-blue-700', confirmado: 'bg-emerald-50 text-emerald-700', concluido: 'bg-slate-100 text-slate-600',
              cancelado: 'bg-red-50 text-red-600', reagendado: 'bg-amber-50 text-amber-700',
            } as Record<string, string>)[statusKey] || 'bg-slate-100 text-slate-600';
            const person = selectedAppointment.clients?.name || selectedAppointment.prospect_name;
            const rows = [
              { icon: Clock, label: 'Duração', value: formatDuration(selectedAppointment.duration) },
              selectedAppointment.location ? { icon: MapPin, label: 'Local', value: selectedAppointment.location } : null,
              person ? { icon: Users, label: selectedAppointment.clients ? 'Cliente' : 'Contato', value: person } : null,
            ].filter(Boolean) as { icon: any; label: string; value: string }[];
            return (
              <>
                <DialogHeader className="relative -m-6 mb-2 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
                  <img src="/imgs/logos allana/letra_A_logo.png" alt="" aria-hidden="true" className="pointer-events-none absolute -right-4 top-1/2 h-36 w-36 -translate-y-1/2 select-none object-contain opacity-[0.12]" />
                  <div className="relative flex items-center gap-4 text-left">
                    <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-2xl border border-[#c4ab83]/40 bg-white shadow-sm">
                      <span className="text-[10px] font-semibold uppercase leading-none text-[#9a7f52]">{format(appointmentDate, 'MMM', { locale: ptBR }).replace('.', '')}</span>
                      <span className="text-xl font-semibold leading-tight tabular-nums text-slate-900">{format(appointmentDate, 'dd')}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">{typeLabel}</p>
                      <DialogTitle className="truncate text-2xl font-semibold tracking-tight text-slate-900">{selectedAppointment.title}</DialogTitle>
                      <p className="mt-0.5 text-sm capitalize text-slate-500">{format(appointmentDate, "EEEE, d 'de' MMMM 'às' HH:mm", { locale: ptBR })}</p>
                    </div>
                  </div>
                </DialogHeader>

                <div className="space-y-4 pt-4">
                  <div className="flex flex-wrap gap-1.5">
                    <span className={`rounded-full px-2.5 py-0.5 text-[12px] font-medium ${statusTone}`}>{selectedAppointment.status}</span>
                    {selectedAppointment.priority && selectedAppointment.priority !== 'normal' && (
                      <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[12px] font-medium capitalize text-amber-700">Prioridade {selectedAppointment.priority}</span>
                    )}
                  </div>

                  <dl className="divide-y divide-slate-100 rounded-xl border border-slate-200/80">
                    {rows.map((row) => (
                      <div key={row.label} className="flex items-center gap-3 px-4 py-3">
                        <row.icon className="h-4 w-4 shrink-0 text-[#c4ab83]" />
                        <dt className="w-20 shrink-0 text-xs font-medium text-slate-400">{row.label}</dt>
                        <dd className="min-w-0 flex-1 truncate text-sm text-slate-800" title={row.value}>{row.value}</dd>
                      </div>
                    ))}
                    {selectedAppointment.cases && (
                      <button
                        type="button"
                        onClick={() => { setShowAppointmentModal(false); navigate(`/processo/${selectedAppointment.case_id}`); }}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[#faf7f2]"
                      >
                        <FileText className="h-4 w-4 shrink-0 text-[#c4ab83]" />
                        <span className="w-20 shrink-0 text-xs font-medium text-slate-400">Processo</span>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#7a6440]" title={selectedAppointment.cases.title}>{selectedAppointment.cases.title}</span>
                        <span className="text-xs text-[#9a7f52]">Abrir →</span>
                      </button>
                    )}
                  </dl>

                  {selectedAppointment.description && (
                    <div className="rounded-xl bg-[#faf7f2] px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9a7f52]">Descrição</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{selectedAppointment.description}</p>
                    </div>
                  )}

                  {selectedAppointment.status === 'Concluído' && appointmentCompletion && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3">
                      <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-800">
                        <CheckCircle className="h-4 w-4" /> Finalizado em {format(new Date(appointmentCompletion.completed_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </p>
                      {appointmentCompletion.completion_report && (
                        <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-emerald-900/80">{appointmentCompletion.completion_report}</p>
                      )}
                    </div>
                  )}

                  <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
                    <Button
                      variant="outline"
                      onClick={() => { setShowAppointmentModal(false); setAppointmentCompletion(null); }}
                      className="rounded-xl"
                    >
                      Fechar
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleEditAppointment(selectedAppointment)}
                      className="rounded-xl border-slate-200 text-slate-700 shadow-none hover:border-[#c4ab83] hover:bg-[#faf7f2]"
                    >
                      <Edit3 className="mr-2 h-4 w-4 text-[#9a7f52]" />
                      Editar
                    </Button>
                    {selectedAppointment.status !== 'Concluído' && (
                      <Button onClick={() => setShowCompletionModal(true)} className="rounded-xl bg-slate-900 text-white hover:bg-slate-800">
                        <CheckCircle className="mr-2 h-4 w-4 text-[#e8d5a8]" />
                        Finalizar
                      </Button>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Modal para Criar Compromisso */}
      {clickedDate && (
        <CreateAppointmentModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          selectedDate={clickedDate}
          onAppointmentCreated={handleAppointmentCreated}
        />
      )}

      {/* Modal para Criar Tarefa */}
      {clickedDate && (
        <CreateTaskModal
          isOpen={showCreateTaskModal}
          onClose={() => setShowCreateTaskModal(false)}
          selectedDate={clickedDate}
          onTaskCreated={handleTaskCreated}
        />
      )}

      {/* Modal para Editar Compromisso */}
      {editingAppointment && (
        <CreateAppointmentModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          selectedDate={new Date(editingAppointment.appointment_date)}
          onAppointmentCreated={handleAppointmentUpdated}
          initialData={editingAppointment}
        />
      )}

      {/* Modal de Finalização do Compromisso */}
      <Dialog open={showCompletionModal} onOpenChange={setShowCompletionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Finalizar Compromisso
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="completion-report">Relatório de Finalização</Label>
              <Textarea
                id="completion-report"
                placeholder="Descreva o que foi realizado neste compromisso..."
                value={completionReport}
                onChange={(e) => setCompletionReport(e.target.value)}
                className="mt-2"
                rows={4}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCompletionModal(false);
                  setCompletionReport("");
                }}
                disabled={isCompleting}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleCompleteAppointment}
                disabled={isCompleting || !completionReport.trim()}
                className="bg-green-600 hover:bg-green-700"
              >
                {isCompleting ? "Finalizando..." : "Finalizar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Agenda;
